/**
 * Utility functions for parsing and converting time durations
 */

/**
 * Converts a time duration string to minutes for comparison
 * Supports formats like: "6.9 h", "7 m", "70 d", "7.1 d", "2 mo", "1.2 y"
 * @param timeString - The time duration string to parse
 * @returns Number of minutes for comparison, or 0 if invalid
 */
export const parseDurationToMinutes = (timeString: string | null | undefined): number => {
  if (!timeString || typeof timeString !== 'string') {
    return 0;
  }

  // Remove extra whitespace and convert to lowercase for easier parsing
  const cleanString = timeString.trim().toLowerCase();

  // Extract the numeric value and unit using regex
  const match = cleanString.match(/^(\d+(?:\.\d+)?)\s*([a-z]+)$/);

  if (!match) {
    return 0;
  }

  const value = parseFloat(match[1]);
  const unit = match[2];

  // Convert to minutes based on unit
  switch (unit) {
    case 'm':
    case 'min':
    case 'minute':
    case 'minutes':
      return value;

    case 'h':
    case 'hr':
    case 'hour':
    case 'hours':
      return value * 60;

    case 'd':
    case 'day':
    case 'days':
      return value * 60 * 24;

    case 'mo':
    case 'month':
    case 'months':
      return value * 60 * 24 * 30;

    case 'y':
    case 'yr':
    case 'year':
    case 'years':
      return value * 60 * 24 * 365;

    default:
      return 0;
  }
};

/**
 * Custom sort function for time duration strings
 * @param rowA - First row to compare
 * @param rowB - Second row to compare
 * @param columnId - The column ID being sorted
 * @returns Comparison result (-1, 0, 1)
 */
export const sortTimeInState = (rowA: any, rowB: any, columnId: string): number => {
  const rowAValue = rowA.values ? rowA.values[columnId] : rowA.getValue(columnId);
  const rowBValue = rowB.values ? rowB.values[columnId] : rowB.getValue(columnId);
  const valueA = parseDurationToMinutes(rowAValue);
  const valueB = parseDurationToMinutes(rowBValue);

  if (valueA < valueB) return -1;
  if (valueA > valueB) return 1;
  return 0;
};

/**
 * Converts a date string to a Date object for comparison
 * Handles various date formats and null/undefined values
 * @param dateString - The date string to parse
 * @returns Date object for comparison, or null for invalid dates
 */
export const parseDate = (dateString: string | null | undefined): Date | null => {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  // Remove extra whitespace
  const cleanString = dateString.trim();

  // Handle common "no data" indicators
  if (
    cleanString === '' ||
    cleanString === '-' ||
    cleanString.toLowerCase() === 'n/a' ||
    cleanString.toLowerCase() === 'null'
  ) {
    return null;
  }

  const date = new Date(cleanString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return null;
  }

  return date;
};

/**
 * Custom sort function for date strings
 * @param rowA - First row to compare
 * @param rowB - Second row to compare
 * @param columnId - The column ID being sorted
 * @returns Comparison result (-1, 0, 1)
 */
export const sortDateColumn = (rowA: any, rowB: any, columnId: string): number => {
  const dateAValue = rowA.values ? rowA.values[columnId] : rowA.getValue(columnId);
  const dateBValue = rowB.values ? rowB.values[columnId] : rowB.getValue(columnId);
  const dateA = parseDate(dateAValue);
  const dateB = parseDate(dateBValue);

  // Handle null values - put them at the end
  if (dateA === null && dateB === null) return 0;
  if (dateA === null) return 1;
  if (dateB === null) return -1;

  // Compare valid dates
  if (dateA.getTime() < dateB.getTime()) return -1;
  if (dateA.getTime() > dateB.getTime()) return 1;
  return 0;
};

/**
 * Converts a string value to a number for numeric comparison
 * Handles null/undefined values and non-numeric strings
 * @param value - The value to parse as a number
 * @returns Number for comparison, or null for invalid numbers
 */
export const parseNumericValue = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    return isNaN(value) ? null : value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  // Remove extra whitespace
  const cleanString = value.trim();

  // Handle common "no data" indicators
  if (
    cleanString === '' ||
    cleanString === '-' ||
    cleanString.toLowerCase() === 'n/a' ||
    cleanString.toLowerCase() === 'null'
  ) {
    return null;
  }

  const parsed = parseFloat(cleanString);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Custom sort function for numeric values (including negative numbers)
 * @param rowA - First row to compare
 * @param rowB - Second row to compare
 * @param columnId - The column ID being sorted
 * @returns Comparison result (-1, 0, 1)
 */
export const sortNumericColumn = (rowA: any, rowB: any, columnId: string): number => {
  const numericAValue = rowA.values ? rowA.values[columnId] : rowA.getValue(columnId);
  const numericBValue = rowB.values ? rowB.values[columnId] : rowB.getValue(columnId);
  const valueA = parseNumericValue(numericAValue);
  const valueB = parseNumericValue(numericBValue);

  // Handle null values - put them at the end
  if (valueA === null && valueB === null) return 0;
  if (valueA === null) return 1;
  if (valueB === null) return -1;

  // Compare numeric values
  if (valueA < valueB) return -1;
  if (valueA > valueB) return 1;
  return 0;
};

/**
 * Determines the type of address value for sorting priority
 * @param value - The address value to categorize
 * @returns Object with type and parsed value for comparison
 */
export const categorizeAddressValue = (
  value: string | number | null | undefined,
): {
  type: 'null' | 'numeric' | 'ip' | 'alphanumeric';
  sortValue: number | string | null;
} => {
  if (value === null || value === undefined) {
    return { type: 'null', sortValue: null };
  }

  let stringValue: string;
  if (typeof value === 'number') {
    stringValue = value.toString();
  } else {
    stringValue = value.toString().trim();
  }

  // Handle empty or null-like values
  if (
    stringValue === '' ||
    stringValue === '-' ||
    stringValue.toLowerCase() === 'n/a' ||
    stringValue.toLowerCase() === 'null'
  ) {
    return { type: 'null', sortValue: null };
  }

  // Check if it's a pure number (including negative numbers and decimals)
  const numericMatch = stringValue.match(/^-?\d+(\.\d+)?$/);
  if (numericMatch) {
    return { type: 'numeric', sortValue: parseFloat(stringValue) };
  }

  // Check if it's an IP address (basic IPv4 pattern)
  const ipMatch = stringValue.match(/^\d+\.\d+\.\d+\.\d+$/);
  if (ipMatch) {
    // Convert IP to a comparable format by treating each octet as a number
    const octets = stringValue.split('.').map((octet) => parseInt(octet, 10));
    // Create a comparable value: first octet * 256^3 + second octet * 256^2 + etc.
    const ipValue = octets[0] * 16777216 + octets[1] * 65536 + octets[2] * 256 + octets[3];
    return { type: 'ip', sortValue: ipValue };
  }

  // Everything else is alphanumeric
  return { type: 'alphanumeric', sortValue: stringValue.toLowerCase() };
};

/**
 * Custom sort function for address values (numeric, IP, alphanumeric)
 * Priority order: 1. Numeric, 2. IP addresses, 3. Alphanumeric, 4. Null values
 * @param rowA - First row to compare
 * @param rowB - Second row to compare
 * @param columnId - The column ID being sorted
 * @returns Comparison result (-1, 0, 1)
 */
export const sortAddressColumn = (rowA: any, rowB: any, columnId: string): number => {
  const addressAValue = rowA.values ? rowA.values[columnId] : rowA.getValue(columnId);
  const addressBValue = rowB.values ? rowB.values[columnId] : rowB.getValue(columnId);
  const categoryA = categorizeAddressValue(addressAValue);
  const categoryB = categorizeAddressValue(addressBValue);

  // Define type priority (lower number = higher priority)
  const typePriority = {
    numeric: 1,
    ip: 2,
    alphanumeric: 3,
    null: 4,
  };

  const priorityA = typePriority[categoryA.type];
  const priorityB = typePriority[categoryB.type];

  // If different types, sort by priority
  if (priorityA !== priorityB) {
    return priorityA - priorityB;
  }

  // Same type, compare values
  if (categoryA.type === 'null') {
    return 0; // Both null
  }

  if (categoryA.type === 'numeric' || categoryA.type === 'ip') {
    const numA = categoryA.sortValue as number;
    const numB = categoryB.sortValue as number;
    if (numA < numB) return -1;
    if (numA > numB) return 1;
    return 0;
  }

  if (categoryA.type === 'alphanumeric') {
    const strA = categoryA.sortValue as string;
    const strB = categoryB.sortValue as string;
    return strA.localeCompare(strB);
  }

  return 0;
};
